import { filter as _filter } from "lodash";
import type { Emitter } from "mitt";
import mitt from "mitt";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import { ref } from "vue";
import type { FilterResponse } from "../../responses/FilterResponse";

type Events = {
    updatedFilter: undefined;
    deletedFilters: undefined;
};

type InitialFilterList = { [key: string]: string };

export type FilterList = { [key: string]: string | string[] };

export const useFilterStore = defineStore("filter", () => {
    const events: Emitter<Events> = mitt<Events>();
    const filter: Ref<string> = ref("");
    const filters: Ref<FilterList> = ref({});

    const regularFilters: Ref<FilterResponse[]> = ref([]);
    const defaultRadius: Ref<number> = ref(10);

    const setFilterValue = (
        name: string,
        value: string | string[],
        emit: boolean,
    ) => {
        filters.value[name] = value;

        if (emit) {
            events.emit("updatedFilter");
        }
    };

    const getFilter = (name: string) => {
        return filters.value[name];
    };

    const getFilterCount = () => {
        return Object.keys(filters.value).length;
    };

    const hasFilter = (name: string) => {
        return Object.hasOwn(filters.value, name);
    };

    const hasFilterWithValue = (name: string, value: string) => {
        if (hasFilter(name)) {
            const filterValue = getFilter(name);

            if (Array.isArray(filterValue)) {
                return filterValue.includes(value);
            }

            return filterValue === value;
        }

        return false;
    };

    const removeFilterWithValue = (name: string, value: string) => {
        if (hasFilterWithValue(name, value)) {
            let filter = getFilter(name);

            if (Array.isArray(filter)) {
                filter = _filter(filter, (item) => value !== item);
            }

            if (filter.length >= 1) {
                setFilterValue(name, filter, false);
            } else {
                removeFilter(name);
            }
        }
    };

    const addOrRemoveFilter = (name: string, value: string) => {
        if (hasFilterWithValue(name, value)) {
            removeFilterWithValue(name, value);
        } else {
            addFilter(name, value);
        }

        events.emit("updatedFilter");
    };

    const removeFilter = (name: string) => {
        if (hasFilter(name)) {
            delete filters.value[name];
        }
    };

    const resetFilters = () => {
        for (const filterToDelete in filters.value) {
            delete filters.value[filterToDelete];
        }

        events.emit("updatedFilter");
        events.emit("deletedFilters");
    };

    const addFilter = (name: string, value: string) => {
        if (!(typeof value === "string")) {
            console.error(`Only strings allowed to add as filter (${name})`);
            return this;
        }

        let currentValue = filters.value[name];

        if (Array.isArray(currentValue)) {
            if (!Object.hasOwn(currentValue, value)) {
                currentValue.push(value);
            }
        } else {
            if (currentValue !== undefined) {
                currentValue = [currentValue, value];
            } else {
                currentValue = [value];
            }
        }

        setFilterValue(name, currentValue, false);
    };

    const toUrlString = (
        additionalFilters: { [key: string]: string } | null = null,
        rawFilter: boolean = false,
        pushToHistory: boolean = false,
    ) => {
        const hash = window.location.hash;
        let params = filters.value;
        let url = "";

        if (additionalFilters && typeof additionalFilters === "object") {
            params = { ...params, ...additionalFilters };
        }

        params = Object.keys(params).reduce((obj: FilterList, key: string) => {
            const value = params[key];

            if (
                value !== undefined &&
                value !== null &&
                value !== "" &&
                value !== 0
            ) {
                obj[key] = String(value);
            }

            return obj;
        }, {});

        url += new URLSearchParams(params).toString();

        if (url !== "" && !url.includes("?")) {
            url = `?` + url;
        }
        if (hash !== "") {
            url += hash;
        }

        if (pushToHistory) {
            if (url.length > 0) {
                history.replaceState({}, "", url);
            } else {
                //only remember utm_ params for SEO/SEA
                const params = new URLSearchParams(window.location.search);
                const utmParams = new URLSearchParams();

                for (const [key, value] of params.entries()) {
                    if (key.startsWith("utm_") && value.trim() !== "") {
                        utmParams.set(key, value);
                    }
                }

                const newUrl = utmParams.toString()
                    ? `${location.pathname}?${utmParams.toString()}`
                    : location.pathname;

                history.replaceState({}, "", newUrl);
            }
        }

        return url;
    };

    const toRawUrlString = (
        additionalFilters: { [key: string]: string } | null = null,
    ) => {
        return toUrlString(additionalFilters, true);
    };

    const setInitialFilters = (filters: InitialFilterList) => {
        for (const key in filters) {
            const filter = filters[key];
            if (filter !== undefined) {
                setFilterValue(key, filter, false);
            }
        }
    };

    const setFilterOptions = (filters: FilterList[]) => {
        regularFilters.value = filters["regular"];
    };

    return {
        events,
        filter,
        filters,
        regularFilters,
        defaultRadius,
        setFilterValue,
        getFilterCount,
        getFilter,
        hasFilter,
        hasFilterWithValue,
        removeFilterWithValue,
        addOrRemoveFilter,
        removeFilter,
        resetFilters,
        addFilter,
        toUrlString,
        toRawUrlString,
        setInitialFilters,
        setFilterOptions,
    };
});
